import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import BatteryWheelPicker from './BatteryWheelPicker'
import StorageScrollPicker from './StorageScrollPicker'
import { STORAGE_OPTIONS } from '../utils/storageOptions'
import ListingSpecCell from './ListingSpecCell'
import { SpecBatteryIcon } from './ListingSpecIcons'

interface ListingSpecPanelProps {
  variant: 'smartphone' | 'basic'
  storage: string
  onStorageChange: (value: string) => void
  storageOptions?: readonly string[]
  releaseYearLabel: string
  releaseYearWheelValue: number | null
  releaseYearUnknown: boolean
  releaseYearUnknownLabel: string
  releaseYearUnknownButton: ReactNode
  releaseYearPicker: ReactNode
  batteryLevel: number
  batteryTone: string
  onBatteryChange: (value: number) => void
  batteryHint?: string
  /** Compact scroll wheels (desktop listing builder). */
  compactPickers?: boolean
  /** Hide battery health (sell flow step Telefonas). */
  showBattery?: boolean
}

export default function ListingSpecPanel({
  variant,
  storage,
  onStorageChange,
  storageOptions = STORAGE_OPTIONS,
  releaseYearLabel,
  releaseYearWheelValue,
  releaseYearUnknown,
  releaseYearUnknownLabel,
  releaseYearUnknownButton,
  releaseYearPicker,
  batteryLevel,
  batteryTone,
  onBatteryChange,
  batteryHint,
  compactPickers = false,
  showBattery = true,
}: ListingSpecPanelProps) {
  const { t } = useTranslation()
  const isSmartphone = variant === 'smartphone'

  const yearValueChip = releaseYearUnknown ? (
    <span className="listing-spec-value-chip listing-spec-value-chip--muted">{releaseYearUnknownLabel}</span>
  ) : (
    <span className="listing-spec-value-chip">{releaseYearWheelValue ?? '—'}</span>
  )

  return (
    <div className="listing-spec-panel">
      <div
        className={`listing-spec-row${
          isSmartphone && showBattery
            ? ' listing-spec-row--triple'
            : isSmartphone
              ? ' listing-spec-row--dual'
              : ' listing-spec-row--single'
        }`}
      >
        {isSmartphone ? (
          <ListingSpecCell
            tone="storage"
            label={t('listing.storageLabel')}
            valueChip={<span className="listing-spec-value-chip">{storage || '—'}</span>}
          >
            <StorageScrollPicker
              value={storage}
              onChange={onStorageChange}
              options={storageOptions}
              ariaLabel={t('listing.storageLabel')}
              compact={compactPickers}
            />
          </ListingSpecCell>
        ) : null}
        <ListingSpecCell
          tone="year"
          label={releaseYearLabel}
          valueChip={yearValueChip}
          footer={releaseYearUnknownButton}
        >
          {releaseYearPicker}
        </ListingSpecCell>
        {isSmartphone && showBattery ? (
          <ListingSpecCell
            tone="battery"
            label={t('listing.form.batteryLabel')}
            valueChip={
              <span
                className="listing-spec-value-chip listing-spec-value-chip--battery"
                style={{ color: batteryTone, borderColor: `${batteryTone}55` }}
              >
                <SpecBatteryIcon level={batteryLevel} size={20} />
                <span>{batteryLevel}%</span>
              </span>
            }
          >
            <BatteryWheelPicker compact value={batteryLevel} onChange={onBatteryChange} />
          </ListingSpecCell>
        ) : null}
      </div>
      {isSmartphone && batteryHint ? <p className="listing-spec-row-hint">{batteryHint}</p> : null}
    </div>
  )
}
